import type {
  BaseCollectionConfig,
  ChangeMessage,
  CollectionConfig,
  InferSchemaOutput,
  StandardSchemaAlias,
  SyncConfig,
} from '@tanstack/db';
import { DuplicateKeySyncError } from '@tanstack/db';
import { compact, omit } from 'es-toolkit';
import type { FilledMangoQuery, RxCollection, RxDocumentData } from 'rxdb/plugins/core';
import {
  clone,
  ensureNotFalsy,
  getFromMapOrCreate,
  lastOfArray,
  prepareQuery,
  rxStorageWriteErrorToRxError,
} from 'rxdb/plugins/core';
import { isRecord } from '@/lib/utils/type-guards';

const RESERVED_RXDB_FIELDS = ['_rev', '_deleted', '_attachments', '_meta'] as const;

const stripRxdbFields = <T extends Record<string, unknown>>(value: RxDocumentData<T>): T =>
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- RxDocumentData<T> is T plus RxDB meta.
  omit(value, RESERVED_RXDB_FIELDS) as unknown as T;

type RxSubscription = { unsubscribe: () => void };

export const OPEN_RXDB_SUBSCRIPTIONS = new WeakMap<RxCollection, Set<RxSubscription>>();

const isDuplicateKeySyncError = (error: unknown): error is DuplicateKeySyncError =>
  error instanceof DuplicateKeySyncError ||
  (error instanceof Error && error.message.includes('already exists in the collection'));

type SchemaRow<TSchema extends StandardSchemaAlias> = InferSchemaOutput<TSchema> & Record<string, unknown>;

export type RxDBCollectionConfig<
  T extends Record<string, unknown> = Record<string, unknown>,
  TSchema extends StandardSchemaAlias = never,
> = Omit<BaseCollectionConfig<T, string, TSchema>, 'onInsert' | 'onUpdate' | 'onDelete' | 'getKey'> & {
  rxCollection: RxCollection<T, unknown, unknown, unknown>;
  syncBatchSize?: number;
};

/* eslint-disable no-redeclare -- TypeScript overloads */
export function rxdbCollectionOptions<TSchema extends StandardSchemaAlias>(
  config: RxDBCollectionConfig<SchemaRow<TSchema>, TSchema>
): CollectionConfig<SchemaRow<TSchema>, string, TSchema> & { schema: TSchema };
export function rxdbCollectionOptions<T extends Record<string, unknown>>(
  config: RxDBCollectionConfig<T> & { schema?: never }
): CollectionConfig<T, string> & { schema?: never };
export function rxdbCollectionOptions<T extends Record<string, unknown>, TSchema extends StandardSchemaAlias>(
  config: RxDBCollectionConfig<T, TSchema>
) {
  type Row = T;
  type Key = string;

  const { ...restConfig } = config;
  const rxCollection = config.rxCollection;

  const primaryPath = rxCollection.schema.primaryPath;
  const getKey = (item: Row): string => String(item[primaryPath]);

  type WriteMessage = Omit<ChangeMessage<Row, Key>, 'key'>;
  type SyncParams = Parameters<SyncConfig<Row, Key>['sync']>[0];
  const sync: SyncConfig<Row, Key> = {
    sync: (params: SyncParams) => {
      const { collection, begin, write, commit, markReady } = params;
      const seenKeys = new Set<string>();
      let ready = false;

      const normalizeMessage = (message: WriteMessage) => {
        if (!isRecord(message.value)) {
          return message;
        }
        const key = getKey(message.value);
        if (message.type === 'delete') {
          seenKeys.delete(key);
          return message;
        }
        if (message.type === 'insert') {
          if (seenKeys.has(key) || collection.has(key)) {
            return { ...message, type: 'update' } as WriteMessage;
          }
          seenKeys.add(key);
          return message;
        }
        if (message.type === 'update') {
          seenKeys.add(key);
        }
        return message;
      };

      const writeWithFallback = (message: WriteMessage) => {
        try {
          write(message);
        } catch (error) {
          if (message.type !== 'insert' || !isDuplicateKeySyncError(error) || !isRecord(message.value)) {
            throw error;
          }
          const key = getKey(message.value);
          seenKeys.add(key);
          write({ ...message, type: 'update' });
        }
      };

      const buffer: WriteMessage[] = [];

      const enqueue = (message: WriteMessage) => {
        if (!ready) {
          buffer.push(message);
          return;
        }
        const normalized = normalizeMessage(message);
        begin();
        try {
          writeWithFallback(normalized);
        } finally {
          commit();
        }
      };

      async function initialFetch() {
        let cursor: RxDocumentData<Row> | undefined;
        const syncBatchSize = config.syncBatchSize ?? 1000;
        begin();

        while (!ready) {
          // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion -- RxDB Mango types do not accept computed selectors.
          const query = (
            cursor
              ? {
                  selector: {
                    $or: [
                      { '_meta.lwt': { $gt: cursor._meta.lwt } },
                      {
                        '_meta.lwt': cursor._meta.lwt,
                        [primaryPath]: { $gt: getKey(cursor) },
                      },
                    ],
                  },
                  sort: [{ '_meta.lwt': 'asc' }, { [primaryPath]: 'asc' }],
                  limit: syncBatchSize,
                  skip: 0,
                }
              : {
                  selector: {},
                  sort: [{ '_meta.lwt': 'asc' }, { [primaryPath]: 'asc' }],
                  limit: syncBatchSize,
                  skip: 0,
                }
          ) as FilledMangoQuery<Row>;

          const preparedQuery = prepareQuery<Row>(rxCollection.storageInstance.schema, query);
          // biome-ignore lint/performance/noAwaitInLoops: cursor paging must be sequential
          const result = await rxCollection.storageInstance.query(preparedQuery);
          const docs = compact(
            result.documents.map((documentData) => (isRecord(documentData) ? documentData : undefined))
          );

          cursor = lastOfArray(docs);
          if (docs.length === 0) {
            ready = true;
            break;
          }

          for (const documentData of docs) {
            const cloned = clone(documentData);
            if (!isRecord(cloned)) {
              continue;
            }
            enqueue({ type: 'insert', value: stripRxdbFields(cloned) });
          }
        }
        commit();
      }

      let sub: RxSubscription | null = null;
      function startOngoingFetch() {
        const subscription = rxCollection.$.subscribe((event) => {
          const currentCandidate = clone(event.documentData);
          if (!isRecord(currentCandidate)) {
            return;
          }
          const current = stripRxdbFields(currentCandidate);
          switch (event.operation) {
            case 'INSERT': {
              enqueue({ type: 'insert', value: current });
              break;
            }
            case 'UPDATE': {
              enqueue({ type: 'update', value: current });
              break;
            }
            case 'DELETE': {
              enqueue({ type: 'delete', value: current });
              break;
            }
            default: {
              break;
            }
          }
        });

        if (!isRecord(subscription) || typeof subscription.unsubscribe !== 'function') {
          return;
        }
        sub = subscription;
        const subs = getFromMapOrCreate(OPEN_RXDB_SUBSCRIPTIONS, rxCollection, () => new Set());
        subs.add(subscription);
      }

      async function start() {
        startOngoingFetch();
        await initialFetch();

        if (buffer.length > 0) {
          begin();
          for (const msg of buffer) {
            writeWithFallback(normalizeMessage(msg));
          }
          commit();
          buffer.length = 0;
        }

        markReady();
      }

      start().catch((error: unknown) => {
        console.warn('RxDB sync failed to start', error);
      });

      return () => {
        if (!sub) {
          return;
        }
        const subs = getFromMapOrCreate(OPEN_RXDB_SUBSCRIPTIONS, rxCollection, () => new Set());
        subs.delete(sub);
        sub.unsubscribe();
      };
    },
  };

  const collectionConfig: CollectionConfig<Row, string, TSchema> = {
    ...restConfig,
    getKey,
    sync,
    onInsert: async (params) => {
      const newItems = params.transaction.mutations.map((mutation) => mutation.modified);
      const result = await rxCollection.bulkUpsert(newItems);
      if (result.error.length > 0) {
        throw rxStorageWriteErrorToRxError(ensureNotFalsy(result.error[0]));
      }
      return result.success;
    },
    onUpdate: async (params) => {
      const mutations = params.transaction.mutations.filter((mutation) => mutation.type === 'update');

      await Promise.all(
        mutations.map(async (mutation) => {
          if (!isRecord(mutation.modified)) {
            return;
          }
          const newValue = mutation.modified;
          const id = getKey(newValue);
          const documentRecord = await rxCollection.findOne(id).exec();
          if (!documentRecord) {
            return;
          }
          await documentRecord.incrementalPatch(newValue);
        })
      );
    },
    onDelete: async (params) => {
      const mutations = params.transaction.mutations.filter((mutation) => mutation.type === 'delete');
      const ids = compact(
        mutations.map((mutation) => (isRecord(mutation.original) ? getKey(mutation.original) : undefined))
      );
      const result = await rxCollection.bulkRemove(ids);
      if (result.error.length > 0) {
        throw rxStorageWriteErrorToRxError(ensureNotFalsy(result.error[0]));
      }
      return result.success;
    },
  };
  return collectionConfig;
}
/* eslint-enable no-redeclare */
